import { createContext, useContext, useState } from "react";
import { MarkerColorType } from "@newComponents/common/Viewer/types";
import { UseBoolean, useBoolean } from "@newHooks/common/useBoolean";
import { useDocumentContext } from "./document.context";
import { DocumentSignerAttrTarget, DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";

export type DocumentMarker = {
  id?: bigint;
  target: DocumentSignerAttrTarget.Signer;
  signerIndex: number;
  signerColor?: MarkerColorType;
  signerUuid: string;
  attr: DocumentSignerAttrType;
  attrUuid: string;
  groupUuid: string;
  value?: string;
  page: number | null;
  x: number | null;
  y: number | null;
};

interface DocumentMarkerContextProps {
  markers: DocumentMarker[];
  setMarkers: (markers: DocumentMarker[]) => void;
  isAdvancedMode: UseBoolean;

  currentLinkingIdx: number;
  currentLinkingAttr: DocumentSignerAttrType | null;
  currentLinkingAttrUuid: string | null;
}

const DocumentMarkerContext = createContext<DocumentMarkerContextProps>({
  markers: [],
  setMarkers: () => {},
  isAdvancedMode: {
    value: false,
    onTrue: () => {},
    onFalse: () => {},
    onToggle: () => {},
    setValue: () => {},
  },
  currentLinkingIdx: -1,
  currentLinkingAttr: null,
  currentLinkingAttrUuid: null,
});

function DocumentMarkerProvider({ markers: markersProp = [], children }: { markers?: DocumentMarker[]; children: React.ReactNode }) {
  const { mode } = useDocumentContext();
  const [markers, setMarkers] = useState<DocumentMarker[]>(markersProp);

  const isAdvancedMode = useBoolean(!!markers.find((e) => e.attr === DocumentSignerAttrType.DynamicSigner));

  return (
    <DocumentMarkerContext.Provider
      value={{
        markers,
        setMarkers,
        isAdvancedMode,
        currentLinkingIdx: typeof mode === "object" && mode.type === "LINKING" ? mode.signerIndex : -1,
        currentLinkingAttr: typeof mode === "object" && mode.type === "LINKING" ? mode.attr : null,
        currentLinkingAttrUuid: typeof mode === "object" && mode.type === "LINKING" ? mode.attrUuid : null,
      }}
    >
      {children}
    </DocumentMarkerContext.Provider>
  );
}

function useDocumentMarkerContext() {
  return useContext(DocumentMarkerContext);
}

export { DocumentMarkerContext, DocumentMarkerProvider, useDocumentMarkerContext };
